import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchTablePreference,
  saveTablePreference,
} from '~/redux/actions/table-preferences-actions';
import { getDefaultVisibleColumns } from '~/utils/tablePreferences';

/**
 * The columns a user shows on a screen, saved on the server per user.
 *
 * `visibleColumns` (column fields) is the screen's default columns until the server's value arrives.
 * `save(visibleColumns)` resolves to true when saved; saving the default columns resets the choice.
 */
const useTablePreferences = (screen) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.userReducers.user?.id);
  const preference = useSelector((state) => state.tablePreferencesReducers.screens[screen]);

  const isLoaded = Boolean(preference?.loaded);
  const defaultVisibleColumns = useMemo(() => getDefaultVisibleColumns(screen), [screen]);

  useEffect(() => {
    if (userId && !isLoaded) {
      dispatch(fetchTablePreference(screen));
    }
  }, [dispatch, userId, screen, isLoaded]);

  const save = useCallback(
    (nextVisibleColumns) => dispatch(saveTablePreference(screen, nextVisibleColumns)),
    [dispatch, screen]
  );

  return {
    visibleColumns: isLoaded ? preference.visibleColumns : defaultVisibleColumns,
    defaultVisibleColumns,
    saving: Boolean(preference?.saving),
    save,
  };
};

export default useTablePreferences;
